import { Calendar, CheckCircle2, DollarSign, User } from 'lucide-react'
import { formatAmount } from '@/lib/utils/formatters'
import type { PaymentLogDetailResponse } from '../../types/api'
import { CHARGEBACK_DETAIL_CARD_CLASS } from '../../constants/chargebackDetailConstants'
import { display, formatDate } from '../../utils/chargebackDetailFormatters'

export function ChargebackDetailSummary({
  applicantId,
  detail,
}: {
  applicantId: string
  detail: PaymentLogDetailResponse | null
}) {
  const progress = detail?.chargeback_type || detail?.chargeback_status

  return (
    <section className={`${CHARGEBACK_DETAIL_CARD_CLASS} p-4 sm:p-5`}>
      <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 lg:grid-cols-5 lg:divide-x lg:divide-slate-100">
        {/* 1. Applicant ID */}
        <div className="flex items-center gap-3.5 lg:pr-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#1597ff]">
            <User className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Applicant ID
            </span>
            <span className="block text-sm font-bold text-slate-900 sm:text-base">
              {display(applicantId)}
            </span>
          </div>
        </div>

        {/* 2. Chargeback Progress */}
        <div className="flex flex-col justify-center lg:px-4">
          <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
            Chargeback Progress
          </span>
          <div className="flex items-center gap-1.5 pt-0.5">
            {progress ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden />
            ) : null}
            <span className="truncate text-sm font-bold text-slate-900 sm:text-base">
              {display(progress)}
            </span>
          </div>
        </div>

        {/* 3. Response Deadline */}
        <div className="flex items-center gap-3.5 lg:px-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#1597ff]">
            <Calendar className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Response Deadline
            </span>
            <span className="block text-sm font-bold text-slate-900 sm:text-base">
              {formatDate(detail?.response_deadline)}
            </span>
          </div>
        </div>

        {/* 4. Chargeback Amount */}
        <div className="flex items-center gap-3.5 lg:px-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f5ebff] text-purple-600">
            <DollarSign className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Chargeback Amount
            </span>
            <span className="block text-sm font-bold text-slate-900 sm:text-base">
              {formatAmount(detail?.adjustment_amount ?? 0)}
            </span>
          </div>
        </div>

        {/* 5. Last Updated By */}
        <div className="flex items-center gap-3.5 lg:pl-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <User className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Last Updated By
            </span>
            <span className="block truncate text-sm font-bold text-slate-900 sm:text-base">
              {display(detail?.payment_adjustment_history?.[0]?.username)}
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}
